import { Diversity3, Edit, Search } from "@mui/icons-material";
import {
    Box,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useEffect, useState } from "react";

import { CustomerAppLabel } from "../../components/CustomerAppLabel";
import { HeaderPage } from "../../components/Header/HeaderPage";
import PageWrapper from "../../components/PageWrapper";
import TableDataGrid from "../../components/TableDataGrid";
import Toast from "../../components/Toast";
import { findAcquirer } from "../../helpers/customer";
import { formatTransactionFee } from "../../helpers/transaction";
import { usePaginatedData } from "../../hooks/usePaginatedData";
import { useToast } from "../../hooks/useToast";
import { Customer } from "../../interfaces/customer";
import { AcquirerEditModal } from "./AcquirerEditModal";

export default function AcquirerAppCustomer() {
    const [openModal, setOpenModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState<Customer | null>(null);
    const [search, setSearch] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    const { toast, closeToast, updateToast } = useToast();

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            setSearch(searchTerm);
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const { data, isLoading, setPage, meta, refresh } =
        usePaginatedData<Customer>(
            `/customers/pagination?include=acquirers&name=${search}`
        );

    const handleEdit = (row: Customer) => {
        setSelectedRow(row);
        setOpenModal(true);
    };

    const handleEditSuccess = () => {
        refresh();

        updateToast({
            open: true,
            severity: "success",
            text: "Configurações salvas com sucesso!",
        });
    };

    const handleEditError = (data: any, error: string) => {
        updateToast({
            open: true,
            severity: "error",
            text: error,
        });
    };

    const columns: GridColDef<Customer>[] = [
        {
            field: "name",
            headerName: "Cliente/App",
            flex: 1.5,
            sortable: false,
            renderCell: ({ row }) => (
                <CustomerAppLabel
                    customer={row?.name || ""}
                    app={row?.project_name || ""}
                />
            ),
        },
        {
            field: "pix_acquirer",
            headerName: "Adquirente PIX",
            sortable: false,
            flex: 1,
            renderCell: (params) => (
                <Typography variant="body2">
                    {findAcquirer(params.row, "pix")?.name || ""}
                </Typography>
            ),
        },
        {
            field: "card_acquirer",
            headerName: "Adquirente Cartão",
            sortable: false,
            flex: 1,
            renderCell: (params) => (
                <Typography variant="body2">
                    {findAcquirer(params.row, "card")?.name || ""}
                </Typography>
            ),
        },
        {
            field: "pix_fee",
            headerName: "Taxa PIX",
            sortable: false,
            flex: 1,
            renderCell: (params) => {
                const acquirer = findAcquirer(params.row, "pix");
                return formatTransactionFee(
                    acquirer?.customer_acquirer?.fee_percentage,
                    acquirer?.customer_acquirer?.fixed_fee
                );
            },
        },
        {
            field: "card_fee",
            headerName: "Taxa Cartão",
            sortable: false,
            flex: 1,
            renderCell: (params) => {
                const acquirer = findAcquirer(params.row, "card");
                return formatTransactionFee(
                    acquirer?.customer_acquirer?.fee_percentage,
                    acquirer?.customer_acquirer?.fixed_fee
                );
            },
        },
        {
            field: "actions",
            headerName: "Ações",
            sortable: false,
            disableExport: true,
            align: "right",
            headerAlign: "right",
            width: 80,
            renderCell: (params) => (
                <IconButton
                    onClick={() => handleEdit(params.row)}
                    color="primary"
                >
                    <Edit />
                </IconButton>
            ),
        },
    ];

    return (
        <PageWrapper>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-end"
            >
                <HeaderPage
                    title="Cliente/App"
                    subtitle="Gerencie as adquirentes e taxas de seus clientes"
                    icon={<Diversity3 />}
                />

                <TextField
                    size="small"
                    placeholder="Pesquisar por nome..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ minWidth: 300 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <Box mt={5}>
                <div className="containerListTablePagesGlobal">
                    <TableDataGrid
                        rows={data}
                        columns={columns}
                        loading={isLoading}
                        hideColumnMenu
                        hideExports
                        hideFilter
                        paginationMode="server"
                        rowCount={meta.total}
                        page={meta.current_page - 1}
                        onPageChange={(newPage) => setPage(newPage + 1)}
                        pageSize={meta.per_page}
                    />
                </div>
            </Box>

            <AcquirerEditModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                data={selectedRow}
                onSuccess={handleEditSuccess}
                onError={handleEditError}
            />

            <Toast
                open={toast.open}
                onClose={closeToast}
                severity={toast.severity}
                text={toast?.text}
            />
        </PageWrapper>
    );
}
